import { AlertCircle, Edit2, List, Plus, RefreshCw, Repeat, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';
import { Transaction } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface ExpenditureTabProps {
  exchangeRate: number;
  localCurrency: string;
  overseasCurrency: string;
  transactions: Transaction[];
  setIsExchangeRateModalOpen: (isOpen: boolean) => void; // This will now open settings
  groupedExpenditures: Transaction[];
  onAddTransaction: () => void;
  onEditTransaction: (t: Transaction) => void;
  onDeleteTransaction: (id: string) => void;
  onOpenSubscriptions: () => void;
  isPrivacyMode: boolean;
}

export function ExpenditureTab({
  exchangeRate,
  localCurrency,
  overseasCurrency,
  transactions,
  setIsExchangeRateModalOpen,
  groupedExpenditures,
  onAddTransaction,
  onEditTransaction,
  onDeleteTransaction,
  onOpenSubscriptions,
  isPrivacyMode,
}: ExpenditureTabProps) {
  return (
    <div className="space-y-4">
      {exchangeRate === 1 && transactions.some((t) => t.category === 'Overseas Expenses') && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50 border border-amber-100 p-4 rounded-2xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-amber-900">Exchange Rate Not Set</p>
            <p className="text-[10px] text-amber-700">Support transactions are currently using 1:1 rate. Please set the monthly rate.</p>
          </div>
          <button
            onClick={() => setIsExchangeRateModalOpen(true)}
            className="px-3 py-1.5 bg-amber-500 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider shadow-sm active:scale-95 transition-transform"
          >
            Set Rate
          </button>
        </motion.div>
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h3 className="font-bold text-lg text-zinc-900 dark:text-zinc-100">Expenditure</h3>
          <button
            onClick={() => setIsExchangeRateModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Rate: {exchangeRate}
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={onOpenSubscriptions} className="w-10 h-10 bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 rounded-xl flex items-center justify-center shadow-sm hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
            <Repeat className="w-5 h-5" />
          </button>
          <button onClick={onAddTransaction} className="w-10 h-10 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl flex items-center justify-center shadow-lg">
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
      {groupedExpenditures.map((exp) => (
        <div key={exp.id} className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-zinc-100 dark:border-zinc-800 shadow-sm flex items-center group transition-all hover:border-indigo-100 dark:hover:border-indigo-900/50">
          <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl flex items-center justify-center shrink-0">
            <List className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1 ml-3">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{exp.note || 'Untitled'}</p>
              <div className="text-right">
                <p className={cn("text-sm font-bold text-zinc-900 dark:text-zinc-100", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(exp.amount, localCurrency)}</p>
                {exp.isSummary && exp.nairaAmount && exp.nairaAmount > 0 && <p className={cn("text-[10px] font-medium text-zinc-400", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(exp.nairaAmount, overseasCurrency)}</p>}
              </div>
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-400 font-medium">{exp.date}</span>
                <span className="w-1 h-1 bg-zinc-200 rounded-full" />
                <span className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">{exp.category}</span>
              </div>
              {!exp.isSummary && (
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); onEditTransaction(exp); }} className="p-2 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
                  {!exp.id.startsWith('default-') && (
                    <button onClick={(e) => { e.stopPropagation(); onDeleteTransaction(exp.id); }} className="p-2 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}